import { FormProvider } from 'react-hook-form'
import { useZodForm } from '@lib/form/useZodForm'
import { useState } from 'react'
import styles from './styles.module.scss'
import { SubmitButton } from '@components/ui/Button'
import ButtonMinimal from '@components/ui/Button/ButtonMinimal'
import { AddressFormWrapper } from './AddressFormWrapper'
import { CustomerDetailsData, CustomerDetailsSchema } from '../config'
import { useAuth } from '@api/accounts/auth/hooks/useAuth'
import { ErrorField } from './shared/InputField/InputErrorMessage'
import { SubmitButtonState } from '@components/ui/Button/SubmitButton'
import { customerUpdate } from '../api/customerNamesUpdate'
import { customerEmailUpdate } from '../api/customerEmailUpdate'
import { getErrorMessage } from '@api/graphqlShopify'
import { getShopLocale } from '@helpers/env'
import { ISO_3166_COUNTRIES } from '@consts/index'
import FormGrid from '@components/ui/formElements/form/shared/FormGrid'
import { InputField } from '@components/ui/formElements/form/InputField'
import FormButtonGroup from '@components/ui/formElements/form/shared/FormButtonGroup'

const normalize = (value: string | null | undefined): string | null =>
  value === '' || value == null ? null : value

function getPhoneCountryList() {
  return Object.entries(ISO_3166_COUNTRIES).map(([_, { alpha2, prefix }]) => {
    return {
      value: alpha2,
      label: prefix,
      availability: true
    }
  })
}

export function ManageCustomerDetails({
  drawerState,
  handleCloseAccountDetailsDrawer
}: {
  drawerState: { data: CustomerDetailsData }
  handleCloseAccountDetailsDrawer: () => void
}) {
  const phoneCountryList = getPhoneCountryList()
  const { refreshUserProfile, makeRequestWithRefreshCheck, token } = useAuth()
  const [buttonState, setButtonState] = useState<SubmitButtonState>('ready')
  const originalCustomer = drawerState.data

  const form = useZodForm(CustomerDetailsSchema, {
    defaultValues: { ...drawerState.data }
  })

  const {
    handleSubmit,
    setError,
    formState: { errors, isSubmitting }
  } = form

  const handleUpdateCustomer = async (data: CustomerDetailsData) => {
    setButtonState('loading')
    try {
      await makeRequestWithRefreshCheck(customerUpdate, data)

      const isUpdatedEmail =
        normalize(data?.email) !== normalize(originalCustomer?.email)
      const isUpdatedPhone =
        normalize(data?.phoneNumber) !==
        normalize(originalCustomer?.phoneNumber)

      if (
        (isUpdatedEmail || isUpdatedPhone) &&
        typeof token === 'string' &&
        !!token
      ) {
        await customerEmailUpdate({
          email: data.email,
          accessToken: token,
          phoneNumber: data?.phoneNumber
        })
      }

      await refreshUserProfile()

      setButtonState('ready')
      handleCloseAccountDetailsDrawer()
    } catch (e) {
      const error = e as unknown as Error
      const errorMessage =
        getErrorMessage(error) || 'Something went wrong. Try again later.'
      setError(
        'root.customError',
        {
          type: 'focus',
          message: errorMessage
        },
        {
          shouldFocus: true
        }
      )
      setButtonState('ready')
    } finally {
      setButtonState('ready')
    }
  }

  const defaultPhonePlaceholder =
    phoneCountryList.find(
      country => country.value === getShopLocale().toUpperCase()
    )?.label || '+44'

  return (
    <FormProvider {...form}>
      <AddressFormWrapper title="Personal">
        <form onSubmit={handleSubmit(handleUpdateCustomer)} noValidate>
          <div className={styles.AccountForm__content}>
            <FormGrid>
              <InputField
                label="First Name"
                name="firstName"
                type="text"
                placeholder="First Name"
              />
              <InputField
                label="Last Name"
                name="lastName"
                type="text"
                placeholder="Last Name"
              />
              <InputField
                label="Email"
                name="email"
                type="email"
                placeholder="Email"
                required={true}
              />
              <InputField
                name="phoneNumber"
                type="text"
                label="Phone number"
                placeholder={defaultPhonePlaceholder}
              />
            </FormGrid>
          </div>
          <div className={styles.AccountForm__Buttons}>
            {errors.root?.customError?.message && (
              <ErrorField message={errors.root?.customError.message} />
            )}
            <FormButtonGroup
              variant="primary-inline"
              primaryCta={
                <SubmitButton
                  type="submit"
                  fullWidth="never"
                  state={buttonState}
                  disabled={isSubmitting || buttonState === 'loading'}
                >
                  Save
                </SubmitButton>
              }
              secondaryCta={
                <ButtonMinimal
                  underlined={true}
                  onClick={() => handleCloseAccountDetailsDrawer()}
                >
                  Cancel
                </ButtonMinimal>
              }
            />
          </div>
        </form>
      </AddressFormWrapper>
    </FormProvider>
  )
}
